/**
 * ManagementSidebar Component
 *
 * Navigation sidebar for Platform Management Console
 * Purple theme with administrative navigation items
 * Supports mobile slide-out and desktop fixed modes
 */

import { NavLink, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ChartPieIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  CloudArrowUpIcon,
  UsersIcon,
  CreditCardIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Activity } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
  requiresPermission?: string;
  badge?: number;
}

interface ManagementSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ManagementSidebar: React.FC<ManagementSidebarProps> = ({ isOpen = false, onClose }) => {
  const { hasPermission } = useAuth();
  const location = useLocation();

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (isOpen && onClose) {
      onClose();
    }
  }, [location.pathname]);

  const navigationItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/management',
      icon: ChartBarIcon,
      description: 'Platform overview & analytics',
      requiresPermission: 'platform.analytics.view'
    },
    {
      name: 'Enterprises',
      href: '/management/enterprises',
      icon: BuildingOffice2Icon,
      description: 'Enterprise management',
      requiresPermission: 'platform.enterprises.view'
    },
    {
      name: 'Channel Partners',
      href: '/management/partners',
      icon: UsersIcon,
      description: 'Partner management',
      requiresPermission: 'platform.partners.view'
    },
    {
      name: 'Organizations',
      href: '/management/organizations',
      icon: BuildingOfficeIcon,
      description: 'Manage customer organizations',
      // Without a permission here the filter renders this item for every role that reaches the
      // console, including one whose token cannot load the page.
      requiresPermission: 'platform.organizations.view'
    },
    {
      name: 'Users',
      href: '/management/users',
      icon: UserGroupIcon,
      description: 'Platform user management',
      requiresPermission: 'platform.users.view'
    },
    {
      name: 'Usage',
      href: '/management/usage',
      icon: ChartPieIcon,
      description: 'Encounters & minutes by partner, org and provider',
      // The permission the rollup endpoint requires, not platform.usage.view: that one guards a
      // cross-tenant billing summary and stays ungranted to the console roles.
      requiresPermission: 'platform.usage.report'
    },
    {
      name: 'Subscriptions',
      href: '/management/subscriptions',
      icon: CreditCardIcon,
      description: 'Plans & billing',
      // The plan catalog is scoped per caller. Billing below stays on the wider grant.
      requiresPermission: 'platform.plans.view'
    },
    {
      name: 'Billing',
      href: '/management/billing',
      icon: BanknotesIcon,
      description: 'Revenue & billing dashboard',
      requiresPermission: 'platform.subscriptions.manage'
    },
    {
      name: 'Compliance',
      href: '/management/compliance',
      icon: ShieldExclamationIcon,
      description: 'System integrity & approvals',
      requiresPermission: 'platform.system.configure'
    },
    {
      name: 'AI Costs',
      href: '/management/ai-costs',
      icon: SparklesIcon,
      description: 'AI pipeline analytics',
      requiresPermission: 'platform.ai_analytics.view'
    },
    {
      name: 'External Integrations',
      href: '/management/external-integrations',
      icon: CloudArrowUpIcon,
      description: 'Service accounts & ingestion',
      requiresPermission: 'platform.external_integrations.manage'
    },
    {
      name: 'Coverage Audit',
      href: '/management/coverage-audit',
      icon: ClipboardDocumentCheckIcon,
      description: 'Issue review & seed-detector audits',
      requiresPermission: 'platform.coverage_audit.view'
    },
    {
      name: 'Operations',
      href: '/management/operations',
      icon: MagnifyingGlassCircleIcon,
      description: 'Operators, encounters & pipeline health',
      requiresPermission: 'platform.triage.view'
    },
    {
      name: 'Settings',
      href: '/management/settings',
      icon: Cog6ToothIcon,
      description: 'System configuration',
      requiresPermission: 'platform.system.configure'
    }
  ];

  // Filter items based on permissions
  const visibleItems = navigationItems.filter(
    item => !item.requiresPermission || hasPermission(item.requiresPermission)
  );

  const sidebarContent = (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-b from-purple-900 to-purple-800 px-6 pb-4">
      {/* Logo & Title - Clickable, navigates to /management */}
      <div className="flex h-16 shrink-0 items-center justify-between pt-4">
        <Link
          to="/management"
          className="flex items-center space-x-3 group cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
        >
          <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg transition-all duration-300 group-hover:bg-white/20 group-hover:shadow-lg group-hover:shadow-purple-500/20">
            <ShieldCheckIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">Platform Console</h1>
            <p className="text-purple-200 text-xs">Management Portal</p>
          </div>
        </Link>

        {/* Close button (mobile only) */}
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-2 rounded-lg text-purple-200 hover:bg-purple-700 hover:text-white transition-colors min-h-11 min-w-11 flex items-center justify-center"
            aria-label="Close menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {visibleItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      end={item.href === '/management'}
                      className={({ isActive }) =>
                        `group flex gap-x-3 rounded-md p-3 text-sm font-semibold leading-6 transition-all min-h-11 ${
                          isActive
                            ? 'bg-purple-700 text-white shadow-lg'
                            : 'text-purple-100 hover:bg-purple-700/50 hover:text-white'
                        }`
                      }
                    >
                      <Icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                      <div className="flex flex-col flex-1">
                        <div className="flex items-center justify-between">
                          <span>{item.name}</span>
                          {item.badge && (
                            <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-purple-200 font-normal">
                          {item.description}
                        </span>
                      </div>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Footer Branding */}
          <li className="mt-auto">
            <div className="flex items-center gap-x-3 rounded-md bg-purple-950/50 p-3 text-xs text-purple-200">
              <Activity className="h-4 w-4" />
              <div>
                <p className="font-semibold text-white">Amplify by CareTracker</p>
                <p className="text-purple-300">Enterprise Platform</p>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar - fixed */}
      <div className="hidden md:fixed md:inset-y-0 md:z-50 md:flex md:w-64 md:flex-col">
        {sidebarContent}
      </div>

      {/* Mobile sidebar - slide out */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </div>
    </>
  );
};

export default ManagementSidebar;
